import Link from 'next/link';
import { Download } from 'lucide-react';

import type { WorkspaceFile } from '../../lib/workspace-files';
import { previewImageUrl } from '../../lib/artifact-image';
import { filePreviewLines } from '../../lib/file-preview-text';
import { pdfPreviewUrl } from '../../lib/pdf-preview';
import { PdfThumb } from './PdfThumb';
import { RichFilePreview } from './RichFilePreview';
import { ConvertedThumb } from './ConvertedThumb';
import { convertibleThumb } from '../../lib/converted-document';
import { previewRichKind } from '../../lib/file-preview-text';
import { effectiveContent } from '../../lib/file-record';
import { downloadArtifact } from '../../lib/artifact-file';
import { useNarrow } from '../../lib/use-narrow';
import { useTranslation } from '../../lib/i18n';
import { AppThumb, FilePreviewPage, FileThumb, THUMB_H } from './FileThumb';
import styles from './FileArtifactCard.module.css';
import { Button, Card } from './ui';

// The artifact card shared by the "Agents at work" Files strip and the "Agent Creations" gallery — a
// graphic band (the previewed image when the content is an image URL, else a neutral placeholder well +
// kind glyph), the name, and (gallery only) a bottom-right outlined Download pill. Tapping the
// graphic/name drills into the file-detail page; the Download pill is its own button. Mirrors the
// native `FileCard`.

/** The kind glyph: a 2×2 bento grid for apps, a document outline for files. */
export function FileKindGlyph({ isApp, size = 20 }: { isApp: boolean; size?: number }) {
  const stroke = 'rgba(var(--ink-rgb),0.34)';
  if (isApp) {
    return (
      <svg aria-hidden width={size} height={size} viewBox="0 0 12 12" fill="none">
        {[
          [0.75, 0.75],
          [6.75, 0.75],
          [0.75, 6.75],
          [6.75, 6.75],
        ].map(([x, y], i) => (
          <rect key={i} x={x} y={y} width={4.5} height={4.5} rx={1.4} fill={stroke} />
        ))}
      </svg>
    );
  }
  return (
    <svg aria-hidden width={size} height={size} viewBox="0 0 9 11" fill="none">
      <path
        d="M1 1.4C1 1.18 1.18 1 1.4 1H5.6L8 3.4V9.6C8 9.82 7.82 10 7.6 10H1.4C1.18 10 1 9.82 1 9.6V1.4Z"
        stroke={stroke}
        strokeWidth={0.9}
        strokeLinejoin="round"
      />
      <path d="M5.4 1V3.2C5.4 3.42 5.58 3.6 5.8 3.6H8" stroke={stroke} strokeWidth={0.9} strokeLinejoin="round" />
    </svg>
  );
}

/** The open-external glyph (↗) used by the detail Open button. */
export function OpenExternalGlyph({ size = 16 }: { size?: number }) {
  return (
    <svg aria-hidden width={size} height={size} viewBox="0 0 16 16" fill="none" className="dir-flip" style={{ display: 'block' }}>
      <path d="M6 3.5h6.5V10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.5 3.5L4 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The standalone file-detail page (mobile-web, and the deep-link/refresh target). */
export function fileDetailLink(slug: string, item: WorkspaceFile, basePath: string): { href: React.ComponentProps<typeof Link>['href']; as: string } {
  const query: Record<string, string> = { slug, file: item.name };
  if (item.isApp) query.app = '1';
  const as = `${basePath}/workspace/${encodeURIComponent(slug)}/files/${encodeURIComponent(item.name)}${item.isApp ? '?app=1' : ''}`;
  return { href: { pathname: '/workspace/[slug]/files/[file]', query }, as };
}

/** The link a file card/row uses — the standalone page, at every width.
 *
 *  It used to fork: the per-Space gallery's side panel on desktop, this page when narrow. That gallery is
 *  gone (`/files` is the one Files screen), so the fork had nowhere to send a desktop reader and the
 *  width question no longer has two answers. Kept as its own name rather than folded into
 *  `fileDetailLink` because the call sites mean "wherever a file card leads", which is a decision that
 *  may fork again. */
export function fileLink(slug: string, item: WorkspaceFile, basePath: string): { href: React.ComponentProps<typeof Link>['href']; as: string } {
  return fileDetailLink(slug, item, basePath);
}

/** The standalone file page as a NEW-TAB target — a resolved URL string, not a Next route pair, because a
 *  new tab is a fresh document load with no client-side route to describe.
 *
 *  `standalone=1` is what makes a DESKTOP visit render the page instead of bouncing to the gallery (see the
 *  redirect in `pages/workspace/[slug]/files/[file].tsx`). It lives in the URL rather than in window state on
 *  purpose: the flag then survives a reload, a copy-paste out of the address bar, back/forward and a share —
 *  which is the entire reason this replaced `window.open(blobUrl)`.
 *
 *  Delegates to `fileDetailLink` rather than re-deriving the path, so the mount prefix is applied in exactly
 *  one place. ⚠️ `basePath` is the EMPTY STRING on the root mount, so `basePath || '/orion-web'` is a bug —
 *  `fileDetailLink` already gets this right. */
export function fileNewTabLink(slug: string, item: WorkspaceFile, basePath: string): string {
  const { as } = fileDetailLink(slug, item, basePath);
  return `${as}${as.includes('?') ? '&' : '?'}standalone=1`;
}

/** The graphic band: the previewed image (top-anchored cover) when the content is an image URL, else the
 *  neutral well holding the file's OWN thumbnail — the tilted file-type page the strip card and both phones
 *  draw (`FileThumb`), not a grey outline of a page. Either a fixed `height` (strip) or a 1:1 `square`
 *  (gallery tile).
 *
 *  Exported because the Files list's THUMBNAILS view is the same thumbnail: a file that previews itself in the
 *  workspace gallery must preview itself there too, and a second implementation is how the two drift — which
 *  is exactly what happened, and was reported ("why not using the thumbnail we have in the workspace?"). */
export function FileGraphic({
  item,
  height,
  square,
  thumbHeight = THUMB_H,
  basePath,
  className,
}: {
  item: WorkspaceFile;
  height?: number;
  square?: boolean;
  /** The tilted page's height inside the well. A tile is bigger than the strip's thumb, so it passes its own. */
  thumbHeight?: number;
  /** Mount prefix for the file-type asset — the app runs with no basePath, so same-origin URLs carry it. */
  basePath: string;
  /** The well's SHAPE from the call site — its height/aspect and its corner radius. The Files grid's card
   *  wants a 218px well at radius 20 that becomes a square at radius 12 on a phone, which is a media query
   *  and therefore cannot be an inline `height`. Pass neither `height` nor `square` when you use this. */
  className?: string;
}) {
  const src = previewImageUrl(item);
  const body = effectiveContent(item);
  const previewLines = filePreviewLines(item.name, body, item.contentType);
  const pdfSrc = pdfPreviewUrl(item.name, body, item.contentType);
  const convertible = convertibleThumb(item.name, body, item.contentType);
  return (
    <div
      className={className}
      style={{
        height: square ? undefined : height,
        aspectRatio: square ? '1 / 1' : undefined,
        background: 'rgba(var(--ink-rgb),0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {src ? (
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
      ) : item.isApp ? (
        <AppThumb height={thumbHeight} />
      ) : pdfSrc ? (
        // Page one, rendered by the browser — see `PdfThumb` for the scroll-into-view guard.
        <PdfThumb src={pdfSrc} fallback={<FileThumb item={item} basePath={basePath} height={thumbHeight} />} />
      ) : convertible ? (
        // A `.docx`/`.pptx` cannot be read in a browser — the pod converts it. See `ConvertedThumb`.
        <ConvertedThumb
          url={convertible.url}
          kind={convertible.kind}
          fallback={<FileThumb item={item} basePath={basePath} height={thumbHeight} />}
        />
      ) : previewLines ? (
        // A miniature of the real document — see `RichFilePreview` for why it renders rather than
        // reducing to text.
        // `body ?? ''` only to satisfy the compiler: `filePreviewLines` returns null for empty content, so a
        // non-null `previewLines` already proves there is a body — tsc cannot see that through the call.
        <RichFilePreview kind={previewRichKind(item.name, body, item.contentType)} body={body ?? ''} lines={previewLines} />
      ) : (
        <FileThumb item={item} basePath={basePath} height={thumbHeight} />
      )}
    </div>
  );
}

// A gallery tile (the "Agent Creations" grid): a square image-or-placeholder graphic, the name, and a
// bottom-pinned Share pill. Fills its grid cell (height 100%) and the tap region grows, so the Share
// pills line up across a row regardless of name length. (The Files strip uses `FileStripCard`.)
export function FileArtifactCard({
  item,
  slug,
  basePath,
  onOpen,
  onShare,
  style,
}: {
  item: WorkspaceFile;
  slug: string;
  basePath: string;
  onOpen?: () => void;
  onShare?: () => void;
  style?: React.CSSProperties;
}) {
  const { t } = useTranslation();
  const { href, as } = fileLink(slug, item, basePath);

  return (
    <Card disablePadding interactive className={styles.card} style={{ height: '100%', ...style }}>
      <Link href={href} as={as} onClick={onOpen} style={{ textDecoration: 'none', display: 'block', flex: '1 1 auto' }}>
        <FileGraphic item={item} square basePath={basePath} thumbHeight={104} />
        <div style={{ padding: '14px 16px 0' }}>
          <div
            style={{
              fontSize: 15.5,
              fontWeight: 'var(--sym-weight-semibold)',
              lineHeight: 1.3,
              color: 'rgba(var(--ink-rgb),0.92)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {item.name}
          </div>
        </div>
      </Link>
      <div style={{ padding: '12px 16px 16px', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            onShare?.();
            void downloadArtifact(item);
          }}
        >
          <Download size={16} aria-hidden />
          {t('packs.download')}
        </Button>
      </div>
    </Card>
  );
}
